import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TimelineSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [expandedEvent, setExpandedEvent] = useState(null);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('memorial-language') || 'ar';
    setCurrentLanguage(savedLanguage);

    // Listen for language changes
    const handleLanguageChange = (event) => {
      const newLanguage = event.detail?.language || 'ar';
      setCurrentLanguage(newLanguage);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  const timelineContent = {
    ar: {
      title: "حكاية وطن",
      subtitle: "من شرارة الحرية… إلى فجر التحرير",
      description: "",
      shareText: "شارك هذا الحدث",
      readMoreText: "اقرأ المزيد",
      readLessText: "اقرأ أقل",
      closingText: "في الذكرى الأولى للتحرير، نلتقي لنحفظ المعنى، ونكرّم التضحيات، ونستعيد ما جمع السوريين في تلك اللحظة. وجودكم يمنح هذا اليوم روحه وقيمته.",
      registerButton: "التسجيل لحضور الفعالية"
    },
    en: {
      title: "Story of a Nation",
      subtitle: "From the Spark of Freedom... to the Dawn of Liberation",
      description: "",
      shareText: "Share this event",
      readMoreText: "Read More",
      readLessText: "Read Less",
      closingText: "On the first anniversary of liberation, we meet to preserve the meaning, honor the sacrifices, and reclaim what brought Syrians together in that moment. Your presence gives this day its spirit and value.",
      registerButton: "Register to Attend the Event"
    },
    tr: {
      title: "Bir Ulusun Hikayesi",
      subtitle: "Özgürlüğün Kıvılcımından... Kurtuluşun Şafağına",
      description: "",
      shareText: "Bu olayı paylaş",
      readMoreText: "Devamını Oku",
      readLessText: "Daha Az Oku",
      closingText: "Kurtuluşun ilk yıldönümünde, anlamı korumak, fedakarlıkları onurlandırmak ve Suriyelileri o anda bir araya getiren şeyi geri kazanmak için buluşuyoruz. Varlığınız bu güne ruhunu ve değerini verir.",
      registerButton: "Etkinliğe Katılmak İçin Kayıt Olun"
    }
  };

  const timelineEvents = [
    {
      id: 1,
      date: "2011-03-18",
      dateAr: "١٨ آذار ٢٠١١",
      dateTr: "18 Mart 2011",
      title: "The First Spark",
      titleAr: "الشرارة الأولى",
      titleTr: "İlk Kıvılcım",
      summary: "The day voices demanded freedom",
      summaryAr: "يوم خرجت الحناجر تطالب بالحرية",
      summaryTr: "Seslerin özgürlük talep ettiği gün",
      description: `March 18... the day voices rose demanding freedom, and fear transformed into a cry that broke the wall of silence that had lasted for decades. It was a moment when a single feeling was born: Syrians would never be the same again.`,
      descriptionAr: `١٨ آذار… يوم خرجت الحناجر تطالب بالحرية، وتحوّل الخوف إلى صرخة كسرت جدار الصمت الممتد لعقود. كانت لحظةً وُلد فيها شعورٌ واحد: لن يعود السوريون كما كانوا.`,
      descriptionTr: `18 Mart... seslerin özgürlük talep ettiği gün, korkunun onlarca yıldır süren sessizlik duvarını kıran bir çığlığa dönüştüğü gün. Tek bir duygunun doğduğu bir andı: Suriyeliler artık eskisi gibi olmayacaktı.`,
      icon: "Flag",
      color: "text-primary",
      bgColor: "bg-primary/10"
    },
    {
      id: 2,
      date: "2012",
      dateAr: "٢٠١٢",
      dateTr: "2012",
      title: "Expansion of Movement and Formation of Popular Defense",
      titleAr: "اتساع الحراك وتشكّل الدفاع الشعبي",
      titleTr: "Hareketin Genişlemesi ve Halk Savunmasının Oluşumu",
      summary: "Protests spread and the Free Army forms",
      summaryAr: "امتدّت المظاهرات وتشكّل الجيش الحر",
      summaryTr: "Protestolar yayıldı ve Özgür Ordu oluştu",
      description: `Protests spread to most cities and towns, and repression and arrests increased. As casualties and victims mounted, groups of defected military personnel and civilians began gathering to protect the people... thus forming the military movement that later became known as the "Free Army."`,
      descriptionAr: `امتدّت المظاهرات إلى معظم المدن والبلدات، وتزايدت جرائم القمع والاعتقال. ومع ازدياد الإصابات وسقوط الضحايا، بدأت مجموعات من العسكريين المنشقّين والمدنيين بالتجمّع لحماية الأهالي… فتشكّل الحراك العسكري الذي عُرف لاحقًا باسم "الجيش الحر".`,
      descriptionTr: `Protestolar çoğu şehir ve kasabaya yayıldı, baskı ve tutuklamalar arttı. Yaralılar ve kurbanlar arttıkça, firari askeri personel ve sivillerden oluşan gruplar halkı korumak için toplanmaya başladı... böylece daha sonra "Özgür Ordu" olarak bilinen askeri hareket oluştu.`,
      icon: "Users",
      color: "text-primary",
      bgColor: "bg-primary/10"
    },
    {
      id: 3,
      date: "2013",
      dateAr: "٢٠١٣",
      dateTr: "2013",
      title: "Escalation of Confrontation and Transformation into Battle for Survival",
      titleAr: "تصاعد المواجهة وتحوّل الثورة إلى معركة بقاء",
      titleTr: "Yüzleşmenin Tırmanışı ve Hayatta Kalma Savaşına Dönüşüm",
      summary: "The year of open bloodshed",
      summaryAr: "سنة الدم المفتوح",
      summaryTr: "Açık kan dökme yılı",
      description: `The regime's brutality increased, and the confrontation transformed from protests into open conflict. It was the year of open bloodshed, a year that solidified among Syrians that their cause was just, no matter how long the road.`,
      descriptionAr: `ازدادت قوة النظام في البطش، وتحوّلت المواجهة من مظاهرات إلى صراع مفتوح. كانت سنة الدم المفتوح، وسنةً رسّخت لدى السوريين أن قضيتهم عادلة مهما طال الطريق.`,
      descriptionTr: `Rejimin vahşeti arttı ve yüzleşme protestolardan açık çatışmaya dönüştü. Açık kan dökme yılıydı, Suriyeliler arasında yol ne kadar uzun olursa olsun davalarının adil olduğunu pekiştiren bir yıl.`,
      icon: "AlertTriangle",
      color: "text-error",
      bgColor: "bg-error/10"
    },
    {
      id: 4,
      date: "2014-2015",
      dateAr: "٢٠١٤–٢٠١٥",
      dateTr: "2014-2015",
      title: "Massacres and Forced Displacement",
      titleAr: "المجازر والتهجير القسري",
      titleTr: "Katliamlar ve Zorla Yerinden Edilme",
      summary: "The idea remained greater than everything",
      summaryAr: "بقيت الفكرة أكبر من كل ما حدث",
      summaryTr: "Fikir her şeyden daha büyük kaldı",
      description: `The number of martyrs rose, prisons filled with thousands of detainees, and entire cities were subjected to siege and bombardment. But despite attempts to strangle the revolution, the idea remained greater than everything that happened.`,
      descriptionAr: `ارتفعت أعداد الشهداء، وامتلأت السجون بآلاف المعتقلين، وتعرّضت مدن بأكملها للحصار والقصف. لكن رغم محاولة خنق الثورة، بقيت الفكرة أكبر من كل ما حدث.`,
      descriptionTr: `Şehit sayıları arttı, hapishaneler binlerce tutukluyla doldu ve tüm şehirler kuşatma ve bombardımana maruz kaldı. Ancak devrimi boğma girişimlerine rağmen, fikir olan her şeyden daha büyük kaldı.`,
      icon: "AlertCircle",
      color: "text-error",
      bgColor: "bg-error/10"
    },
    {
      id: 5,
      date: "2016",
      dateAr: "٢٠١٦",
      dateTr: "2016",
      title: "Fall of Aleppo",
      titleAr: "سقوط حلب",
      titleTr: "Halep'in Düşüşü",
      summary: "The fall of the city did not fall with the meaning of the revolution",
      summaryAr: "سقوط المدينة لم يُسقط معنى الثورة",
      summaryTr: "Şehrin düşüşü devrimin anlamını düşürmedi",
      description: `One of the harshest pages... forced displacement, a long night of pain, but the fall of the city did not fall with the meaning of the revolution; instead, it deepened it in the conscience of Syrians.`,
      descriptionAr: `واحدة من أقسى الصفحات… تهجير قسري، وليل طويل من الألم، لكن سقوط المدينة لم يُسقط معنى الثورة، بل عمّقه في ضمير السوريين.`,
      descriptionTr: `En acımasız sayfalardan biri... zorla yerinden edilme, uzun bir acı gecesi, ancak şehrin düşüşü devrimin anlamını düşürmedi; bunun yerine, onu Suriyelilerin vicdanında derinleştirdi.`,
      icon: "Home",
      color: "text-warning",
      bgColor: "bg-warning/10"
    },
    {
      id: 6,
      date: "2017-2018",
      dateAr: "٢٠١٧–٢٠١٨",
      dateTr: "2017-2018",
      title: "Dispersal of Geography and Steadfastness of Meaning",
      titleAr: "تشتّت الجغرافيا وثبات المعنى",
      titleTr: "Coğrafyanın Dağılması ve Anlamın Direnci",
      summary: "The revolution became an idea that cannot be arrested or exiled",
      summaryAr: "الثورة أصبحت فكرة لا يمكن اعتقالها أو نفيها",
      summaryTr: "Devrim tutuklanamaz veya sürgün edilemez bir fikir haline geldi",
      description: `The country was divided between liberated areas, camps, and diaspora, but the story did not end; because the revolution became an idea that cannot be arrested or exiled.`,
      descriptionAr: `توزعت البلاد بين مناطق محررة ومخيمات وشتات، لكن القصة لم تنتهِ؛ لأن الثورة أصبحت فكرة لا يمكن اعتقالها أو نفيها.`,
      descriptionTr: `Ülke kurtarılmış bölgeler, kamplar ve diaspora arasında bölündü, ancak hikaye bitmedi; çünkü devrim tutuklanamaz veya sürgün edilemez bir fikir haline geldi.`,
      icon: "Map",
      color: "text-warning",
      bgColor: "bg-warning/10"
    },
    {
      id: 7,
      date: "2019",
      dateAr: "٢٠١٩",
      dateTr: "2019",
      title: "Beginning of Regime Attacks on Idlib",
      titleAr: "بداية هجمات النظام على إدلب",
      titleTr: "Rejimin İdlib'e Saldırılarının Başlaması",
      summary: "Resilience increases whenever tyranny tries to crush it",
      summaryAr: "صمودٌ يزداد كلما حاول الطغيان سحقه",
      summaryTr: "Zulüm onu ezmeye çalıştıkça direnç artıyor",
      description: `The regime returned to try to control Idlib by force. Intensive bombardment, new displacement, and resilience that increases whenever tyranny tries to crush it.`,
      descriptionAr: `عاد النظام ليحاول السيطرة على إدلب بالقوة. قصفٌ مكثّف، تهجير جديد، وصمودٌ يزداد كلما حاول الطغيان سحقه.`,
      descriptionTr: `Rejim İdlib'i güç kullanarak kontrol etmeye çalışmak için geri döndü. Yoğun bombardıman, yeni yerinden edilme ve zulüm onu ezmeye çalıştıkça artan direnç.`,
      icon: "Target",
      color: "text-error",
      bgColor: "bg-error/10"
    },
    {
      id: 8,
      date: "2020-2022",
      dateAr: "٢٠٢٠–٢٠٢٢",
      dateTr: "2020-2022",
      title: "Years of Pain and Patience",
      titleAr: "سنوات الألم والصبر",
      titleTr: "Acı ve Sabır Yılları",
      summary: "A small hope in the heart that did not extinguish",
      summaryAr: "أمل صغير في القلب… لم ينطفئ",
      summaryTr: "Kalpte sönmeyen küçük bir umut",
      description: `The fronts calmed, but fear, poverty, and displacement did not. Syrians remained suspended between past and future, carrying in their hearts a small hope... that did not extinguish.`,
      descriptionAr: `هدأت الجبهات، لكن لم يهدأ الخوف ولا الفقر ولا النزوح. بقي السوريون معلّقين بين الماضي والمستقبل، يحملون في القلب أملًا صغيرًا… لم ينطفئ.`,
      descriptionTr: `Cepheler sakinleşti, ancak korku, yoksulluk ve yerinden edilme sakinleşmedi. Suriyeliler geçmiş ve gelecek arasında asılı kaldı, kalplerinde sönmeyen küçük bir umut taşıyorlardı.`,
      icon: "Heart",
      color: "text-warning",
      bgColor: "bg-warning/10"
    },
    {
      id: 9,
      date: "2023",
      dateAr: "٢٠٢٣",
      dateTr: "2023",
      title: "Escalation of Drones and Targeting of Civilians",
      titleAr: "تصعيد المسيّرات واستهداف المدنيين",
      titleTr: "İnsansız Hava Araçlarının Tırmanışı ve Sivillerin Hedeflenmesi",
      summary: "The regime has not changed",
      summaryAr: "النظام لم يتغيّر",
      summaryTr: "Rejim değişmedi",
      description: `Drones flew over Idlib bombing markets and camps. Scenes that bring back memories of the first years of oppression, revealing that the regime has not changed, and that silence on brutality is no longer possible.`,
      descriptionAr: `حلّقت المسيّرات فوق إدلب تقصف الأسواق والمخيمات. مشاهد تعيد الذاكرة إلى سنوات القهر الأولى، وتكشف أن النظام لم يتغيّر، وأن الصمت على البطش لم يعد ممكنًا.`,
      descriptionTr: `İnsansız hava araçları İdlib üzerinde uçarak pazarları ve kampları bombaladı. İlk baskı yıllarının anılarını geri getiren sahneler, rejimin değişmediğini ve vahşete karşı sessizliğin artık mümkün olmadığını ortaya koyuyor.`,
      icon: "Zap",
      color: "text-error",
      bgColor: "bg-error/10"
    },
    {
      id: 10,
      date: "2024",
      dateAr: "٢٠٢٤",
      dateTr: "2024",
      title: "Launch of the Battle to Deter Aggression",
      titleAr: "انطلاق معركة ردع العدوان",
      titleTr: "Saldırıyı Caydırma Savaşının Başlaması",
      summary: "We will not leave our people without protection",
      summaryAr: "لن نترك شعبنا بلا حماية",
      summaryTr: "Halkımızı korumasız bırakmayacağız",
      description: `With the repetition of attacks, the revolutionaries gathered to stop the massacre. Thus began the battle to deter aggression; a battle that said with one voice: We will not leave our people without protection.

In the first days of the battle, the fragility of the regime appeared, and its positions collapsed one after another.`,
      descriptionAr: `مع تكرار الاعتداءات، اجتمع الثوّار لوقف المذبحة. فبدأت معركة ردع العدوان؛ معركة قالت بصوت واحد: لن نترك شعبنا بلا حماية.

ومع الأيام الأولى للمعركة، ظهرت هشاشة النظام، وتهاوت مواقعه الواحدة تلو الأخرى.`,
      descriptionTr: `Saldırıların tekrarıyla devrimciler katliamı durdurmak için toplandı. Böylece saldırıyı caydırma savaşı başladı; tek bir sesle söyleyen bir savaş: Halkımızı korumasız bırakmayacağız.

Savaşın ilk günlerinde rejimin kırılganlığı ortaya çıktı ve pozisyonları birbiri ardına çöktü.`,
      icon: "Shield",
      color: "text-primary",
      bgColor: "bg-primary/10"
    },
    {
      id: 11,
      date: "2024-12-08",
      dateAr: "٨ ديسمبر ٢٠٢٤",
      dateTr: "8 Aralık 2024",
      title: "Liberation Day",
      titleAr: "يوم التحرير",
      titleTr: "Kurtuluş Günü",
      summary: "The day Syrians regained their freedom",
      summaryAr: "اليوم الذي استعاد فيه السوريون حريتهم",
      summaryTr: "Suriyelilerin özgürlüklerini geri kazandığı gün",
      description: `The day when Syrians regained their freedom, and the tyranny that had exhausted the country fell. A day that opened the door to the second phase of the path: building the new Syria... a Syria worthy of the sacrifices of its children.`,
      descriptionAr: `اليوم الذي استعاد فيه السوريون حريتهم، وسقط فيه الاستبداد الذي أنهك البلاد. يومٌ فتح الباب نحو المرحلة الثانية من الطريق: بناء سوريا الجديدة… سوريا التي تليق بتضحيات أبنائها.`,
      descriptionTr: `Suriyelilerin özgürlüklerini geri kazandığı ve ülkeyi tüketen zulmün düştüğü gün. Yolun ikinci aşamasına kapıyı açan bir gün: yeni Suriye'yi inşa etmek... çocuklarının fedakarlıklarına layık bir Suriye.`,
      icon: "Sun",
      color: "text-success",
      bgColor: "bg-success/10"
    }
  ];

  const getDate = (event) => {
    switch (currentLanguage) {
      case 'ar': return event?.dateAr;
      case 'tr': return event?.dateTr;
      default: {
        // Handle year-only dates (e.g., "2012", "2013")
        if (event?.date && /^\d{4}$/.test(event.date)) {
          return event.date;
        }
        // Handle date ranges (e.g., "2014-2015", "2017-2018")
        if (event?.date && /^\d{4}-\d{4}$/.test(event.date)) {
          return event.date;
        }
        // Handle full dates
        const date = new Date(event?.date);
        if (date && !isNaN(date.getTime())) {
          return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          });
        }
        return event?.date || '';
      }
    }
  };

  const getTitle = (event) => {
    switch (currentLanguage) {
      case 'ar': return event?.titleAr;
      case 'tr': return event?.titleTr;
      default: return event?.title;
    }
  };

  const getSummary = (event) => {
    switch (currentLanguage) {
      case 'ar': return event?.summaryAr;
      case 'tr': return event?.summaryTr;
      default: return event?.summary;
    }
  };

  const getDescription = (event) => {
    switch (currentLanguage) {
      case 'ar': return event?.descriptionAr;
      case 'tr': return event?.descriptionTr;
      default: return event?.description;
    }
  };

  const toggleExpanded = (eventId) => {
    setExpandedEvent(expandedEvent === eventId ? null : eventId);
  };

  const shareEvent = (event) => {
    if (navigator.share) {
      navigator.share({
        title: getTitle(event),
        text: getSummary(event),
        url: window.location?.href
      });
    } else {
      // Fallback for browsers that don't support Web Share API
      const text = `${getTitle(event)} - ${getSummary(event)} ${window.location?.href}`;
      navigator.clipboard?.writeText(text);
    }
  };

  const content = timelineContent?.[currentLanguage];

  return (
    <section id="timeline" className="py-16 lg:py-24 bg-[#114845]/3">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="font-memorial text-3xl md:text-4xl lg:text-5xl font-bold text-primary mb-4">
            {content?.title}
          </h2>
          <p className="font-body text-xl text-accent mb-6">
            {content?.subtitle}
          </p>
        </motion.div>

        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-border"></div>

          <div className="space-y-8">
            {timelineEvents?.map((event, index) => (
              <motion.div
                key={event?.id}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="relative"
              >
                {/* Timeline Dot */}
                <div className={`absolute left-6 w-4 h-4 ${event?.bgColor} border-2 border-background rounded-full flex items-center justify-center`}>
                  <div className={`w-2 h-2 ${event?.color?.replace('text-', 'bg-')} rounded-full`}></div>
                </div>

                {/* Event Card */}
                <div className="ml-16 bg-card border border-border rounded-lg shadow-memorial overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-4">
                        <div className={`w-10 h-10 ${event?.bgColor} rounded-lg flex items-center justify-center`}>
                          <Icon name={event?.icon} size={20} className={event?.color} />
                        </div>
                        <div>
                          <p className="font-body text-sm text-text-secondary">
                            {getDate(event)}
                          </p>
                          <h3 className="font-body text-lg md:text-xl font-semibold text-primary">
                            {getTitle(event)}
                          </h3>
                        </div>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => shareEvent(event)}
                        iconName="Share2"
                        iconPosition="left"
                        className="flex-shrink-0 share-button"
                      >
                        {content?.shareText}
                      </Button>
                    </div>

                    <p className="font-body text-text-secondary mb-4">
                      {getSummary(event)}
                    </p>

                    {expandedEvent === event?.id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="border-t border-border pt-4 mb-4"
                      >
                        <p className="font-body text-text-primary leading-relaxed whitespace-pre-line">
                          {getDescription(event)}
                        </p>
                      </motion.div>
                    )}

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleExpanded(event?.id)}
                      iconName={expandedEvent === event?.id ? 'ChevronUp' : 'ChevronDown'}
                      iconPosition="right"
                    >
                      {expandedEvent === event?.id ? content?.readLessText : content?.readMoreText}
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Closing Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-12 text-center"
        >
          <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-success/10 border border-primary/20 rounded-lg p-6 md:p-8">
            <p className="font-body text-lg md:text-xl text-text-primary max-w-3xl mx-auto mb-6 leading-relaxed">
              {content?.closingText}
            </p>
            <Button
              variant="default"
              size="lg"
              onClick={() => {
                const element = document.getElementById('get-involved');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              iconName="Calendar"
              iconPosition="left"
              className="mx-auto"
            >
              {content?.registerButton}
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TimelineSection;